import React, { useState, useCallback } from "react";
import { I } from "./utils";
import { BRAND_HEX, BRAND_LIGHT, BRAND_RING } from "./theme";  // ← single import

// ─── ICON ─────────────────────────────────────────────────────────────────────
export const Icon = ({ d, size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d={d} />
  </svg>
);

// ─── TOAST ────────────────────────────────────────────────────────────────────
export const ToastCtx = React.createContext(null);
export const useToast = () => React.useContext(ToastCtx);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((msg, type = "success") => {
    const id = Date.now();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3500);
  }, []);
  return (
    <ToastCtx.Provider value={add}>
      {children}
      <div className="fixed bottom-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} style={{ animation: "toastIn .25s ease" }}
            className={`px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white flex items-center gap-2 pointer-events-auto
              ${t.type === "error" ? "bg-rose-500" : t.type === "warn" ? "bg-amber-500" : "bg-emerald-600"}`}>
            <span>{t.type === "error" ? "✕" : "✓"}</span>{t.msg}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// ─── PRIMITIVES ───────────────────────────────────────────────────────────────
export const Badge = ({ label, colorClass }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${colorClass}`}>
    {label?.replace(/_/g, " ")}
  </span>
);

// Spinner border color → BRAND_HEX via inline style
export const Spinner = ({ full }) => (
  <div className={`flex items-center justify-center ${full ? "py-20" : "py-10"}`}>
    <div
      className="w-7 h-7 border-2 border-t-transparent rounded-full animate-spin"
      style={{ borderColor: `${BRAND_HEX} transparent transparent transparent` }}
    />
  </div>
);

export const ApiError = ({ msg, onRetry }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="w-12 h-12 rounded-2xl bg-rose-50 flex items-center justify-center mb-3 text-rose-400">
      <Icon d={I.warn} size={24} />
    </div>
    <p className="font-semibold text-slate-700 mb-1">Something went wrong</p>
    <p className="text-slate-400 text-sm mb-4 max-w-xs">{msg}</p>
    {onRetry && (
      <Btn variant="secondary" size="sm" onClick={onRetry}>
        <Icon d={I.refresh} size={14} /> Retry
      </Btn>
    )}
  </div>
);

export const Empty = ({ icon, title, sub, action }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4 text-slate-400">
      <Icon d={I[icon] || I.dashboard} size={28} />
    </div>
    <p className="font-semibold text-slate-700 text-base mb-1">{title}</p>
    <p className="text-slate-400 text-sm mb-5 max-w-xs">{sub}</p>
    {action}
  </div>
);

export const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ background: "rgba(15,23,42,.55)", backdropFilter: "blur(4px)" }}>
    <div
      className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] flex flex-col`}
      style={{ animation: "modalIn .2s ease" }}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
        <h2 className="text-lg font-bold text-slate-800 tracking-tight">{title}</h2>
        <button onClick={onClose}
          className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors">
          <Icon d={I.close} size={16} />
        </button>
      </div>
      <div className="overflow-y-auto flex-1 px-6 py-5">{children}</div>
    </div>
  </div>
);

// ─── FORM FOCUS HELPER ────────────────────────────────────────────────────────
// Tailwind can't interpolate runtime values into focus: classes, so we handle
// focus ring/border via JS events using the token directly.
const focusStyle  = { borderColor: BRAND_HEX, boxShadow: `0 0 0 3px ${BRAND_RING}` };
const blurStyle   = { borderColor: "", boxShadow: "" };
const onFocus     = e => Object.assign(e.target.style, focusStyle);
const onBlur      = e => Object.assign(e.target.style, blurStyle);
const INPUT_BASE  = "w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none transition-all placeholder:text-slate-300";

// ─── FORM ELEMENTS ────────────────────────────────────────────────────────────
export const Inp = ({ label, error, ...p }) => (
  <div className="flex flex-col gap-1">
    {label && (
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
        {label}
      </label>
    )}
    <input
      {...p}
      onFocus={onFocus}
      onBlur={onBlur}
      className={`${INPUT_BASE} ${error ? "border-rose-300" : ""} ${p.className || ""}`}
    />
    {error && <span className="text-xs text-rose-500">{error}</span>}
  </div>
);

export const Sel = ({ label, options, ...p }) => (
  <div className="flex flex-col gap-1">
    {label && (
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
        {label}
      </label>
    )}
    <select
      {...p}
      onFocus={onFocus}
      onBlur={onBlur}
      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none transition-all"
    >
      <option value="">Select…</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export const Txa = ({ label, ...p }) => (
  <div className="flex flex-col gap-1">
    {label && (
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
        {label}
      </label>
    )}
    <textarea
      {...p}
      rows={3}
      onFocus={onFocus}
      onBlur={onBlur}
      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none transition-all placeholder:text-slate-300 resize-none"
    />
  </div>
);

// ─── BTN ─────────────────────────────────────────────────────────────────────
// primary variant uses inline style so it always reads BRAND_HEX from theme.js
export const Btn = ({ children, variant = "primary", size = "md", loading: ld, className = "", ...p }) => {
  const isPrimary = variant === "primary";

  const staticVariants = {
    secondary: "bg-slate-100 hover:bg-slate-200 text-slate-700",
    danger:    "bg-rose-50 hover:bg-rose-100 text-rose-600",
    ghost:     "hover:bg-slate-100 text-slate-500",
    success:   "bg-emerald-50 hover:bg-emerald-100 text-emerald-700",
  };

  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-base",
  };

  return (
    <button
      {...p}
      disabled={p.disabled || ld}
      className={`inline-flex items-center gap-2 font-semibold rounded-xl transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${staticVariants[variant] ?? ""} ${sizes[size]} ${className}`}
      // primary color comes from theme token, not a hardcoded Tailwind class
      style={isPrimary ? { background: BRAND_HEX, color: "white", ...p.style } : p.style}
    >
      {ld && (
        <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
      )}
      {children}
    </button>
  );
};

export const Card = ({ children, className = "", ...p }) => (
  <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm ${className}`} {...p}>
    {children}
  </div>
);

export const Pages = ({ page, totalPages, total, onPage, loading }) => (
  <div className="flex items-center gap-2 justify-end mt-4">
    <span className="text-xs text-slate-400 mr-auto">{total} total</span>
    <Btn variant="secondary" size="sm" disabled={page <= 1 || loading} onClick={() => onPage(page - 1)}>←</Btn>
    <span className="text-xs text-slate-500 px-2">Page {page} of {totalPages}</span>
    <Btn variant="secondary" size="sm" disabled={page >= totalPages || loading} onClick={() => onPage(page + 1)}>→</Btn>
  </div>
);

export const StatCard = ({ label, value, icon, color, sub }) => (
  <Card className="p-5 flex items-start gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon d={I[icon]} size={22} className="text-white" />
    </div>
    <div>
      <p className="text-2xl font-bold text-slate-800 leading-none mb-1">
        {value ?? <span className="inline-block w-8 h-6 bg-slate-100 rounded animate-pulse" />}
      </p>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </Card>
);

// PieChart — brand color not used here (uses emerald/rose for income/expense)
// but the profit % label color could optionally use BRAND_HEX if desired
export const PieChart = ({ income = 0, expenses = 0 }) => {
  const total = income + expenses;
  if (!total) return (
    <div className="w-[180px] h-[180px] rounded-full bg-slate-100 flex items-center justify-center text-xs text-slate-400">
      No data
    </div>
  );
  const r = 70, cx = 90, cy = 90, pct = (income / total) * 360;
  const rad = a => (a - 90) * Math.PI / 180;
  const arc = (s, sw, fill) => {
    if (sw >= 360) sw = 359.99;
    const x1 = cx + r * Math.cos(rad(s)), y1 = cy + r * Math.sin(rad(s));
    const x2 = cx + r * Math.cos(rad(s + sw)), y2 = cy + r * Math.sin(rad(s + sw));
    return <path d={`M${cx} ${cy}L${x1} ${y1}A${r} ${r} 0 ${sw > 180 ? 1 : 0} 1 ${x2} ${y2}Z`} fill={fill} />;
  };
  const profitPct = Math.round(((income - expenses) / total) * 100);
  return (
    <svg width="180" height="180" viewBox="0 0 180 180">
      {arc(0, pct, "#10b981")}
      {arc(pct, 360 - pct, "#f43f5e")}
      <circle cx={cx} cy={cy} r={45} fill="white" />
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize="11" fill="#64748b" fontWeight="600">Profit</text>
      {/* profit % uses BRAND_HEX when positive so it matches the theme */}
      <text x={cx} y={cy + 10} textAnchor="middle" fontSize="12" fontWeight="700"
        fill={profitPct >= 0 ? BRAND_HEX : "#f43f5e"}>
        {profitPct}%
      </text>
    </svg>
  );
};

// ─── CONFIRM DIALOG ─────────────────────────────────────────────────────────────
// Promise-based confirmation, styled to match the app (replaces window.confirm).
// Usage inside any component under <ConfirmProvider>:
//   const confirm = useConfirm();
//   if (!(await confirm({ title, message, confirmText, danger: true }))) return;
const ConfirmCtx = React.createContext(null);
export const useConfirm = () => React.useContext(ConfirmCtx);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { opts, resolve } | null

  const confirm = useCallback(
    (opts = {}) => new Promise((resolve) => setState({ opts, resolve })),
    []
  );

  const close = useCallback((result) => {
    setState((s) => {
      if (s) s.resolve(result);
      return null;
    });
  }, []);

  // Keyboard: Escape cancels, Enter confirms.
  React.useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
      if (e.key === "Enter") close(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [state, close]);

  const o = state?.opts || {};
  const danger = o.danger !== false; // default to destructive styling

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: "rgba(15,23,42,.55)", backdropFilter: "blur(4px)" }}
          onClick={() => close(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden"
            style={{ animation: "modalIn .2s ease" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 pt-6 pb-5 flex flex-col items-center text-center">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${
                  danger ? "bg-rose-50 text-rose-500" : "bg-slate-100 text-slate-500"
                }`}
              >
                <Icon d={danger ? I.trash : I.warn} size={22} />
              </div>
              <h2 className="text-base font-bold text-slate-800 mb-1">
                {o.title || "Are you sure?"}
              </h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                {o.message || "This action cannot be undone."}
              </p>
            </div>
            <div className="flex gap-2 px-6 pb-6">
              <Btn variant="secondary" className="flex-1 justify-center" onClick={() => close(false)}>
                {o.cancelText || "Cancel"}
              </Btn>
              <Btn
                variant={danger ? "danger" : "primary"}
                className="flex-1 justify-center"
                onClick={() => close(true)}
              >
                {o.confirmText || "Delete"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}